<template>
    <div class="ui-page">
        <ui-page-header title="Concorrência"
            subtitle="Quem está sendo nomeado, a partir das nomeações de PP e AJ lançadas nos casos. Os nomes vêm do cadastro único de AJs, com as grafias unificadas.">
            <v-btn outlined to="/prospeccao/ajs"><v-icon left small>mdi-domain</v-icon>Administradores judiciais</v-btn>
            <v-btn outlined :disabled="!data" @click="exportRows"><v-icon left small>mdi-microsoft-excel</v-icon>Exportar (.xlsx)</v-btn>
        </ui-page-header>

        <div class="pc-filters mb-4">
            <v-select v-model="filters.period" :items="periodItems" label="Período" dense outlined hide-details :menu-props="{ offsetY: true }" style="max-width: 200px;" @change="load"></v-select>
            <v-select v-model="filters.uf" :items="ufItems" label="UF" dense outlined hide-details clearable :menu-props="{ offsetY: true }" style="max-width: 120px;" @change="load"></v-select>
            <v-select v-model="filters.role" :items="roleItems" label="Papel" dense outlined hide-details :menu-props="{ offsetY: true }" style="max-width: 160px;" @change="load"></v-select>
        </div>

        <template v-if="data">
            <section class="pc-stats mb-5" aria-label="Indicadores de concorrência">
                <div class="pc-stat">
                    <span class="pc-stat__label">Posição da BeJ entre os AJs</span>
                    <span class="pc-stat__value">{{ data.kpis.bej_position ? `${data.kpis.bej_position}º` : "—" }}</span>
                    <span class="pc-stat__hint">{{ data.kpis.bej_aj }} AJ · {{ data.kpis.bej_pp }} PP no período</span>
                </div>
                <div class="pc-stat">
                    <span class="pc-stat__label">AJ concorrente mais nomeado</span>
                    <span class="pc-stat__value">{{ data.kpis.top_competitor ? data.kpis.top_competitor.count : "—" }}</span>
                    <span class="pc-stat__hint">{{ data.kpis.top_competitor ? data.kpis.top_competitor.administrator.name : "sem nomeações" }}</span>
                </div>
                <div class="pc-stat">
                    <span class="pc-stat__label">PP concorrente mais nomeado</span>
                    <span class="pc-stat__value">{{ data.kpis.top_pp_competitor ? data.kpis.top_pp_competitor.count : "—" }}</span>
                    <span class="pc-stat__hint">{{ data.kpis.top_pp_competitor ? data.kpis.top_pp_competitor.administrator.name : "sem nomeações" }}</span>
                </div>
                <div class="pc-stat">
                    <span class="pc-stat__label"><span class="ui-pill__dot ui-pill__dot--danger"></span>Casos perdidos</span>
                    <span class="pc-stat__value">{{ data.kpis.lost }}</span>
                    <span class="pc-stat__hint">BeJ prospectou e outro AJ foi nomeado</span>
                </div>
            </section>

            <div class="pc-grid">
                <ui-section v-if="filters.role !== 'pp'" title="AJs mais nomeados" :description="periodLabel">
                    <ui-empty v-if="!data.ranking_aj.length" text="Nenhuma nomeação de AJ no período." />
                    <div v-for="item in data.ranking_aj" :key="`aj-${item.administrator.id}`" class="pc-bar" :class="{ 'pc-bar--house': item.administrator.is_house }">
                        <span class="pc-bar__name">{{ item.administrator.name }}</span>
                        <span class="pc-bar__track"><span class="pc-bar__fill" :style="{ width: `${(item.count / maxAj) * 100}%` }"></span></span>
                        <span class="pc-bar__count">{{ item.count }}</span>
                    </div>
                </ui-section>
                <ui-section v-if="filters.role !== 'aj'" title="Perícia prévia (PP) · mais nomeados" :description="periodLabel">
                    <ui-empty v-if="!data.ranking_pp.length" text="Nenhuma nomeação de PP no período." />
                    <div v-for="item in data.ranking_pp" :key="`pp-${item.administrator.id}`" class="pc-bar" :class="{ 'pc-bar--house': item.administrator.is_house }">
                        <span class="pc-bar__name">{{ item.administrator.name }}</span>
                        <span class="pc-bar__track"><span class="pc-bar__fill" :style="{ width: `${(item.count / maxPp) * 100}%` }"></span></span>
                        <span class="pc-bar__count">{{ item.count }}</span>
                    </div>
                    <template v-if="data.conversions.length">
                        <div class="ui-divider"></div>
                        <div class="ui-label">Conversão PP → AJ</div>
                        <table class="pc-table">
                            <thead><tr><th>Quem fez a PP</th><th class="r">PP</th><th class="r">Virou AJ</th><th class="r">Aguardando</th><th class="r">Outro AJ</th></tr></thead>
                            <tbody>
                                <tr v-for="item in data.conversions" :key="`conv-${item.administrator.id}`" :class="{ 'pc-row--house': item.administrator.is_house }">
                                    <td>{{ item.administrator.name }}</td>
                                    <td class="r">{{ item.pp }}</td>
                                    <td class="r">{{ item.converted }}</td>
                                    <td class="r">{{ item.pending }}</td>
                                    <td class="r">{{ item.other }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </template>
                </ui-section>
            </div>

            <ui-section title="Quem domina cada UF · AJ nomeado" class="mt-5" :description="`RJ e TCA distribuídas no período, AJ ainda pendente e os três AJs mais nomeados. ${periodLabel}.`">
                <table class="pc-table">
                    <thead><tr><th>UF</th><th class="r">RJ + TCA</th><th class="r">Pendentes AJ</th><th>1º</th><th>2º</th><th>3º</th><th class="r">BeJ</th></tr></thead>
                    <tbody>
                        <tr v-for="row in data.by_uf" :key="row.uf">
                            <td><strong>{{ row.uf }}</strong></td>
                            <td class="r">{{ row.cases }}</td>
                            <td class="r">{{ row.pending_aj }}</td>
                            <td v-for="index in 3" :key="index" :class="{ 'pc-cell--house': row.leaders[index - 1] && row.leaders[index - 1].administrator.is_house }">
                                <span v-if="row.leaders[index - 1]">{{ row.leaders[index - 1].administrator.name }} · {{ row.leaders[index - 1].count }}</span>
                                <span v-else class="ui-muted">—</span>
                            </td>
                            <td class="r">{{ row.bej_aj }}<span v-if="row.bej_pp" class="ui-muted"> · {{ row.bej_pp }} PP</span></td>
                        </tr>
                    </tbody>
                </table>
            </ui-section>

            <ui-section title="Casos perdidos · BeJ prospectou e outro foi nomeado" class="mt-5"
                description="O motivo, preenchido pelo dono do caso, é o que transforma a concorrência em aprendizado para o próximo caso no mesmo juízo.">
                <ui-empty v-if="!data.lost.length" text="Nenhum caso perdido no período." />
                <table v-else class="pc-table">
                    <thead><tr><th>Caso</th><th>Juízo</th><th>Movimento BeJ</th><th>Nomeado</th><th>Data</th><th>Motivo registrado</th></tr></thead>
                    <tbody>
                        <tr v-for="item in data.lost" :key="item.case_id">
                            <td>
                                <router-link :to="`/prospeccao/casos/${item.case_id}`"><strong>{{ item.company_name }}</strong></router-link>
                                <div class="ui-small ui-muted">{{ item.uf || "—" }} · {{ formatMoneyShort(item.amount) }}</div>
                                <ui-badge v-if="item.kind !== 'lost'" :status="item.kind === 'partial_win' ? 'approved' : 'pending'" :text="item.kind === 'partial_win' ? 'Vitória parcial' : 'PP perdida'" />
                            </td>
                            <td>{{ item.court_name || "—" }}<div class="ui-small ui-muted">{{ item.judge_name || "" }}</div></td>
                            <td>{{ item.last_bej_move.type_label }}<div class="ui-small ui-muted">{{ formatFeedDate(item.last_bej_move.occurred_at) }}</div></td>
                            <td>
                                <div v-if="item.pp">PP: {{ item.pp.name }}</div>
                                <div v-if="item.aj">AJ: {{ item.aj.name }}</div>
                            </td>
                            <td class="ui-nowrap">{{ formatFeedDate(item.appointed_at) }}</td>
                            <td>
                                <span v-if="item.lost_reason">{{ item.lost_reason }}</span>
                                <span v-else class="ui-muted">não registrado</span>
                                <v-btn v-if="$allowed('edit')" x-small text @click="openReason(item)">{{ item.lost_reason ? "Editar" : "Registrar" }}</v-btn>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </ui-section>
        </template>

        <v-dialog v-model="reason.open" max-width="520" persistent>
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <div>
                        <h3 class="ui-section-title">Motivo · {{ reason.item ? reason.item.company_name : "" }}</h3>
                        <p class="ui-section-description">O que aprendemos com este juízo? Fica no caso e aparece aqui.</p>
                    </div>
                </div>
                <div class="ui-card__body">
                    <v-textarea v-model="reason.text" outlined dense rows="3" auto-grow hide-details="auto"
                        placeholder="Ex.: Juiz com preferência consolidada. Avaliar visita presencial antes do próximo caso."></v-textarea>
                </div>
                <div class="ui-card__footer">
                    <v-btn outlined :disabled="reason.saving" @click="reason.open = false">Cancelar</v-btn>
                    <v-btn v-if="$allowed('save')" color="primary" depressed :loading="reason.saving" @click="saveReason">Salvar</v-btn>
                </div>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import axios from "axios";
import { downloadXlsxSheets } from "@/common/util/export-xlsx.util";
import { formatFeedDate, formatMoneyShort, notifyError, notifySuccess } from "@/components/prospect/prospect.util";

const UFS = ["AC", "AL", "AM", "AP", "BA", "CE", "DF", "ES", "GO", "MA", "MG", "MS", "MT", "PA", "PB", "PE", "PI", "PR", "RJ", "RN", "RO", "RR", "RS", "SC", "SE", "SP", "TO"];

export default {
    name: "ProspectCompetition",

    data: () => ({
        data: null,
        loading: false,
        filters: { period: "year", uf: null, role: "both" },
        ufItems: UFS,
        roleItems: [{ value: "both", text: "AJ e PP" }, { value: "aj", text: "Só AJ" }, { value: "pp", text: "Só PP" }],
        reason: { open: false, saving: false, item: null, text: "" },
    }),

    computed: {
        periodItems() {
            const periods = this.data ? this.data.periods : { year: "Ano atual", "90d": "Últimos 90 dias", last_year: "Ano anterior", all: "Todo o período" };
            return Object.entries(periods).map(([value, text]) => ({ value, text }));
        },
        periodLabel() {
            const item = this.periodItems.find((option) => option.value === this.filters.period);
            const range = this.data && this.data.range;
            const span = range && range.from ? ` (${formatFeedDate(range.from)} a ${formatFeedDate(range.to)})` : "";
            return `${item ? item.text : ""}${span}${this.filters.uf ? ` · ${this.filters.uf}` : " · todas as UFs"}`;
        },
        maxAj() {
            return Math.max(1, ...(this.data ? this.data.ranking_aj.map((item) => item.count) : [1]));
        },
        maxPp() {
            return Math.max(1, ...(this.data ? this.data.ranking_pp.map((item) => item.count) : [1]));
        },
    },

    created() {
        this.load();
    },

    methods: {
        formatFeedDate,
        formatMoneyShort,

        async load() {
            this.loading = true;
            try {
                const params = { period: this.filters.period, role: this.filters.role };
                if (this.filters.uf) params.uf = this.filters.uf;
                const result = await axios.get("/api/admin/prospect/competition", { params });
                this.data = result.data.content;
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.loading = false;
            }
        },

        openReason(item) {
            this.reason = { open: true, saving: false, item, text: item.lost_reason || "" };
        },

        async saveReason() {
            this.reason.saving = true;
            try {
                await axios.patch(`/api/admin/prospect/cases/${this.reason.item.case_id}`, { lost_reason: this.reason.text });
                this.reason.open = false;
                notifySuccess(this, "Motivo registrado.");
                await this.load();
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.reason.saving = false;
            }
        },

        exportRows() {
            const ranking = (list, role) => list.map((item, index) => ({ Posição: index + 1, Papel: role, Administrador: item.administrator.name, Nomeações: item.count }));
            downloadXlsxSheets([
                { name: "Ranking", rows: [...ranking(this.data.ranking_aj, "AJ"), ...ranking(this.data.ranking_pp, "PP")] },
                {
                    name: "Por UF",
                    rows: this.data.by_uf.map((row) => ({
                        UF: row.uf, "RJ + TCA": row.cases, "Pendentes AJ": row.pending_aj,
                        "1º": row.leaders[0] ? `${row.leaders[0].administrator.name} (${row.leaders[0].count})` : "",
                        "2º": row.leaders[1] ? `${row.leaders[1].administrator.name} (${row.leaders[1].count})` : "",
                        "3º": row.leaders[2] ? `${row.leaders[2].administrator.name} (${row.leaders[2].count})` : "",
                        "BeJ AJ": row.bej_aj, "BeJ PP": row.bej_pp,
                    })),
                },
                {
                    name: "Casos perdidos",
                    rows: this.data.lost.map((item) => ({
                        Caso: item.company_name, UF: item.uf || "", Passivo: item.amount, Juízo: item.court_name || "", Juiz: item.judge_name || "",
                        "Movimento BeJ": `${item.last_bej_move.type_label} ${formatFeedDate(item.last_bej_move.occurred_at)}`,
                        PP: item.pp ? item.pp.name : "", AJ: item.aj ? item.aj.name : "", Data: formatFeedDate(item.appointed_at), Motivo: item.lost_reason || "",
                    })),
                },
            ], "concorrencia");
        },
    },
};
</script>

<style lang="scss" scoped>
@import "../../assets/scss/ui/tokens";

.pc-filters {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 12px;
}

.pc-stats {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    border: 1px solid $ui-border;
    border-radius: $ui-radius;
    background: $ui-bg;

    @media (max-width: 960px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

.pc-stat {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
    padding: 14px 18px;
    border-right: 1px solid $ui-border;

    &:last-child {
        border-right: 0;
    }
}

.pc-stat__label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: $ui-muted;
}

.pc-stat__value {
    font-size: 24px;
    font-weight: 600;
    color: $ui-fg;
}

.pc-stat__hint {
    font-size: 12px;
    color: $ui-muted;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.pc-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 16px;
    align-items: start;

    @media (max-width: 1100px) {
        grid-template-columns: minmax(0, 1fr);
    }
}

// Cartoes lado a lado: sem o espacamento vertical de ".ui-card + .ui-card".
.pc-grid > .ui-card + .ui-card {
    margin-top: 0;
}

.pc-bar {
    display: grid;
    grid-template-columns: minmax(0, 44%) minmax(0, 1fr) 32px;
    align-items: center;
    gap: 10px;
    padding: 5px 0;
    font-size: 13px;
}

.pc-bar__name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.pc-bar__track {
    height: 10px;
    border-radius: 999px;
    background: $ui-border-soft;
    overflow: hidden;
}

.pc-bar__fill {
    display: block;
    height: 100%;
    border-radius: 999px;
    background: nth(map-get($ui-tones, neutral), 3);
}

.pc-bar__count {
    text-align: right;
    font-weight: 600;
}

.pc-bar--house {
    .pc-bar__name {
        font-weight: 600;
        color: $ui-accent;
    }

    .pc-bar__fill {
        background: $ui-accent;
    }
}

.pc-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    th {
        padding: 6px 8px;
        text-align: left;
        font-weight: 500;
        color: $ui-muted;
        border-bottom: 1px solid $ui-border;
    }

    td {
        padding: 8px;
        border-bottom: 1px solid $ui-border-soft;
        vertical-align: top;
    }

    .r {
        text-align: right;
    }
}

.pc-row--house td,
.pc-cell--house {
    color: $ui-accent;
    font-weight: 600;
}
</style>
